import { EnvelopeIcon, UserCircleIcon } from '@phosphor-icons/react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router-dom'
import { compare, NotificationSettings, UnsavedChangesConfirmationModal, UserSettings } from 'ComponentLibrary'
import { UserSettingsModalShell } from 'ComponentLibrary/src/molecules/UserSettings/UserSettingsModalShell'
import { User as UserType, UserSettingsModalFormState, UserSettingsUpdateMetadata } from 'ComponentLibrary/src/molecules/UserSettings/types'
import type { UserSettingsModalTab } from 'ComponentLibrary/src/molecules/UserSettings/types'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { useIamContext } from 'ClientPortal/src/context/IAmContext'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { normalizeLocale } from 'ClientPortal/src/i18n/supportedLocales'
import { NotificationSettingsContent } from 'ClientPortal/src/pages/Settings/NotificationSettingsContent'
import { getLocaleOptions, getUserSettingsCopy } from 'ClientPortal/src/pages/Settings/i18n'
import { useUserSettingsPage } from 'ClientPortal/src/pages/Settings/UserSettingsPage.data'
import invariant from 'tiny-invariant'

type UserSettingsModalProps = {
    selectedTab: UserSettingsModalTab
    onTabChange: (tab: UserSettingsModalTab) => void
    onClose: () => void
}

export function UserSettingsModal({ selectedTab, onTabChange, onClose }: UserSettingsModalProps) {
    const { user, isUserPending } = useIamContext()
    const { t } = useTranslation('pages')
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tValidation } = useTranslation('validation')
    const currentLocale = useCurrentResolvedLocale()
    const { isLocalizationEnabled } = useFlags()
    const {
        teams,
        patchCurrentUser,
        isCustomerInitialLoading,
        isPatchingUser,
        uploadProfileImage,
        isUploadingProfileImage,
        removeProfileImage,
        isRemovingProfileImage,
    } = useUserSettingsPage()
    const formActionsRef = useRef<Partial<Record<UserSettingsModalTab, Pick<UserSettingsModalFormState, 'onCancel' | 'onSave'>>>>({})
    const [formStatuses, setFormStatuses] = useState<
        Partial<Record<UserSettingsModalTab, Pick<UserSettingsModalFormState, 'isDirty' | 'isPersisting'>>>
    >({})
    const pendingNavigationRef = useRef<UserSettingsModalTab | 'close' | null>(null)
    const [pendingNavigation, setPendingNavigation] = useState<UserSettingsModalTab | 'close' | null>(null)

    const tabs = useMemo(
        () => [
            { value: 'profile' as const, label: t('settings.userSettingsPage.modal.tabs.general'), icon: <UserCircleIcon /> },
            { value: 'notifications' as const, label: t('settings.userSettingsPage.modal.tabs.notifications'), icon: <EnvelopeIcon /> },
        ],
        [t]
    )

    const updateUserSettings = (
        { first_name, last_name, salutation, job_title, phone_number, team_uuid, locale }: UserType,
        metadata?: UserSettingsUpdateMetadata
    ) => {
        invariant(user, 'User must be defined')
        const updatedUser = {
            ...user,
            first_name,
            last_name,
            salutation,
            job_title: job_title !== '' ? job_title : undefined,
            phone_number: phone_number !== '' ? phone_number : undefined,
            team_uuid,
        }
        const normalizedLocale = normalizeLocale(locale)
        if (metadata?.isLocaleDirty && normalizedLocale) {
            updatedUser.locale = normalizedLocale
        }
        const operations = compare(user, updatedUser)
        if (operations.length === 0) {
            completePendingNavigation()
            return
        }

        patchCurrentUser({ operations }, { onSuccess: completePendingNavigation })
    }

    const updateNotificationSettings = (newSettings: NotificationSettings) =>
        patchCurrentUser(
            { operations: [{ op: 'replace', path: '/notification_settings', value: newSettings }] },
            { onSuccess: completePendingNavigation }
        )

    const handleFormStateChange = useCallback(
        (tab: UserSettingsModalTab) => (state: UserSettingsModalFormState) => {
            formActionsRef.current[tab] = { onCancel: state.onCancel, onSave: state.onSave }
            setFormStatuses((currentStatuses) => {
                const currentStatus = currentStatuses[tab]
                if (currentStatus?.isDirty === state.isDirty && currentStatus?.isPersisting === state.isPersisting) {
                    return currentStatuses
                }
                return { ...currentStatuses, [tab]: { isDirty: state.isDirty, isPersisting: state.isPersisting } }
            })
        },
        []
    )

    const activeFormState = useMemo<UserSettingsModalFormState | undefined>(() => {
        const status = formStatuses[selectedTab]
        const actions = formActionsRef.current[selectedTab]
        if (!status || !actions) {
            return undefined
        }
        return { ...status, ...actions }
    }, [formStatuses, selectedTab])
    const navigationBlocker = useBlocker(({ historyAction }) => historyAction === 'POP' && activeFormState?.isDirty === true)

    function completeTabChange(tab: UserSettingsModalTab) {
        formActionsRef.current[tab] = undefined
        setFormStatuses((currentStatuses) => {
            const nextStatuses = { ...currentStatuses }
            delete nextStatuses[tab]
            return nextStatuses
        })
        onTabChange(tab)
    }

    function completePendingNavigation() {
        if (navigationBlocker.state === 'blocked') {
            pendingNavigationRef.current = null
            setPendingNavigation(null)
            navigationBlocker.proceed()
            return
        }

        const navigation = pendingNavigationRef.current
        if (navigation === null) {
            return
        }

        pendingNavigationRef.current = null
        setPendingNavigation(null)
        if (navigation === 'close') {
            onClose()
            return
        }
        completeTabChange(navigation)
    }

    const handleTabChange = (tab: UserSettingsModalTab) => {
        if (activeFormState?.isPersisting) {
            return
        }
        if (activeFormState?.isDirty) {
            pendingNavigationRef.current = tab
            setPendingNavigation(tab)
            return
        }
        completeTabChange(tab)
    }

    const handleClose = () => {
        if (activeFormState?.isPersisting) {
            return
        }
        if (activeFormState?.isDirty) {
            pendingNavigationRef.current = 'close'
            setPendingNavigation('close')
            return
        }
        onClose()
    }

    const handleCancel = () => {
        activeFormState?.onCancel()
        onClose()
    }

    const handlePendingNavigationCancel = () => {
        if (navigationBlocker.state === 'blocked') {
            navigationBlocker.reset()
        }
        pendingNavigationRef.current = null
        setPendingNavigation(null)
    }

    const handlePendingNavigationDiscard = () => {
        activeFormState?.onCancel()
        completePendingNavigation()
    }

    return (
        <>
            <UserSettingsModalShell
                isOpen
                title={t('settings.userSettingsPage.modal.title')}
                tabs={tabs}
                selectedTab={selectedTab}
                onTabChange={handleTabChange}
                onClose={handleClose}
                onCancel={handleCancel}
                closeIconTooltip={tTerminology('actions.close')}
                cancelLabel={tTerminology('confirmation.cancel')}
                saveLabel={tTerminology('actions.save')}
                activeFormState={activeFormState}
            >
                {selectedTab === 'profile' && (
                    <UserSettings
                        user={user}
                        teams={teams}
                        updateUserSettings={updateUserSettings}
                        isUpdatingUserSettings={isPatchingUser}
                        isUserPending={isUserPending || isCustomerInitialLoading}
                        copy={getUserSettingsCopy(t, tTerminology, tValidation)}
                        localeOptions={getLocaleOptions(t)}
                        showLocaleSetting={isLocalizationEnabled}
                        currentLocale={currentLocale}
                        layout="modal"
                        uploadImage={(file: File) => uploadProfileImage({ image: file })}
                        isUploadingImage={isUploadingProfileImage}
                        isRemovingImage={isRemovingProfileImage}
                        removeImage={removeProfileImage}
                        onModalFormStateChange={handleFormStateChange('profile')}
                    />
                )}
                {selectedTab === 'notifications' && (
                    <NotificationSettingsContent
                        user={user}
                        updateSettings={updateNotificationSettings}
                        isPatchingUser={isPatchingUser}
                        isLoading={isCustomerInitialLoading}
                        layout="modal"
                        onModalFormStateChange={handleFormStateChange('notifications')}
                    />
                )}
            </UserSettingsModalShell>
            <UnsavedChangesConfirmationModal
                isOpen={pendingNavigation !== null || navigationBlocker.state === 'blocked'}
                isSaving={!!activeFormState?.isPersisting}
                onCancel={handlePendingNavigationCancel}
                onDiscard={handlePendingNavigationDiscard}
                onSave={() => activeFormState?.onSave()}
                title={t('settings.userSettingsPage.modal.unsavedChanges.title')}
                message={t('settings.userSettingsPage.modal.unsavedChanges.message')}
                cancelLabel={tTerminology('actions.cancel')}
                discardLabel={t('settings.userSettingsPage.modal.unsavedChanges.discardAndContinue')}
                saveLabel={t('settings.userSettingsPage.modal.unsavedChanges.saveAndContinue')}
            />
        </>
    )
}
